import { useQuery } from '@tanstack/react-query'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../api/client'
import type { DeliveryAnalyticsOut, DeliveryOutcomeCounts } from '../api/types'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { DateRangeFields } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { downloadCsv } from '../lib/csv'
import { useFilters } from '../lib/filters'
import { count, formatDate, humanise, minutes, percent, shortDate } from '../lib/format'

function successPct(row: DeliveryOutcomeCounts): number | null {
  return row.attempts ? (row.delivered / row.attempts) * 100 : null
}

function days(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)} days`
}

export function DeliveryAnalyticsPage() {
  const { filters } = useFilters()

  const query = useQuery({
    queryKey: ['delivery-analytics', filters.from, filters.to],
    queryFn: () =>
      api.get<DeliveryAnalyticsOut>('/analytics/deliveries', { from_date: filters.from, to_date: filters.to }),
  })

  const data = query.data

  function exportCsv() {
    if (!data) return
    const lines: (string | number)[][] = [
      [`Deliveries ${data.from_date} to ${data.to_date}`],
      [],
      ['Driver', 'Runs', 'Items attempted', 'Delivered', 'Partial', 'Failed', 'Success %', 'Avg run (min)'],
      ...data.drivers.map((row) => [
        row.driver,
        row.runs,
        row.attempts,
        row.delivered,
        row.partial,
        row.undelivered,
        successPct(row)?.toFixed(1) ?? '',
        row.run_duration_avg_minutes ?? '',
      ]),
      [],
      ['Parish', '', 'Items attempted', 'Delivered', 'Partial', 'Failed', 'Success %'],
      ...data.parishes.map((row) => [
        row.parish,
        '',
        row.attempts,
        row.delivered,
        row.partial,
        row.undelivered,
        successPct(row)?.toFixed(1) ?? '',
      ]),
    ]
    downloadCsv(`Deliveries ${data.from_date} to ${data.to_date}.csv`, lines)
  }

  return (
    <>
      <PageHeader
        title="Delivery performance"
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)} · delivery items, Jamaica time`}
      >
        <button className="btn" onClick={exportCsv} disabled={!data?.drivers.length}>
          Export CSV
        </button>
      </PageHeader>

      <div className="content stack">
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <DateRangeFields />
        </div>

        <Panel isLoading={query.isPending} error={query.error}>
          {data ? <DeliveryAnalyticsView data={data} /> : null}
        </Panel>
      </div>
    </>
  )
}

/** KPIs, charts and tables for one range — shared by the live page and the demo. */
export function DeliveryAnalyticsView({ data }: { data: DeliveryAnalyticsOut }) {
  const kpis = data.kpis
  const reasonMax = Math.max(1, ...data.reasons.map((r) => r.undelivered + r.partial))

  return (
    <>
      <div className="grid cols-4">
        <Kpi
          label="Delivered in full"
          value={percent(kpis.success_rate_pct, 1)}
          foot={`${count(kpis.delivered)} of ${count(kpis.attempts)} attempts · ${count(kpis.partial)} partial · ${count(kpis.undelivered)} failed`}
        />
        <Kpi
          label="First-attempt success"
          value={percent(kpis.first_attempt_success_rate_pct, 1)}
          foot={`${count(kpis.first_attempts)} first attempts · ${count(kpis.attempts - kpis.first_attempts)} re-attempts`}
        />
        <Kpi
          label="Approval to delivery"
          value={days(kpis.lead_time_median_days)}
          foot={`Median · average ${days(kpis.lead_time_avg_days)}`}
        />
        <Kpi
          label="Runs completed"
          value={count(kpis.runs_completed)}
          foot={`Average time out ${minutes(kpis.run_duration_avg_minutes)} · inbox now ${count(kpis.backlog_items)}`}
        />
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>By day</h2>
          </div>
          <div className="card-body">
            <Panel isLoading={false} error={null} isEmpty={data.daily.length === 0} emptyText="No outcomes recorded in this period.">
              <div className="chart">
                <ResponsiveContainer>
                  <BarChart data={data.daily} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid {...GRID} vertical={false} />
                    <XAxis dataKey="date" {...AXIS} tickFormatter={shortDate} minTickGap={20} />
                    <YAxis {...AXIS} width={32} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: 'var(--grey-bg)' }} />
                    <Bar dataKey="delivered" name="Delivered" stackId="o" fill="var(--green)" />
                    <Bar dataKey="partial" name="Partial" stackId="o" fill="var(--amber)" />
                    <Bar dataKey="undelivered" name="Failed" stackId="o" fill="var(--red)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Why deliveries fail</h2>
            <span className="hint">Failed and partial items</span>
          </div>
          <div className="card-body flush">
            <Panel isLoading={false} error={null} isEmpty={data.reasons.length === 0} emptyText="No failed or partial deliveries in this period.">
              <div className="table-wrap">
                <table>
                  <tbody>
                    {data.reasons.map((row) => (
                      <tr key={row.reason}>
                        <td className="strong">{humanise(row.reason)}</td>
                        <td style={{ width: '40%' }}>
                          <div className="bar">
                            <span style={{ width: `${((row.undelivered + row.partial) / reasonMax) * 100}%`, background: 'var(--red)' }} />
                          </div>
                        </td>
                        <td className="num">{count(row.undelivered)} failed</td>
                        <td className="num cell-sub">{count(row.partial)} partial</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <OutcomeTable
        title="Drivers"
        column="Driver"
        hint="Credited to the driver who recorded the outcome"
        rows={data.drivers.map((row) => ({ ...row, name: row.driver }))}
        extra={{
          heads: ['Runs', 'Avg run'],
          cells: (row) => [count(row.runs), minutes(row.run_duration_avg_minutes)],
        }}
      />
      <OutcomeTable title="Parishes" column="Parish" rows={data.parishes.map((row) => ({ ...row, name: row.parish }))} />
    </>
  )
}

function OutcomeTable<T extends DeliveryOutcomeCounts & { name: string }>({
  title,
  column,
  hint,
  rows,
  extra,
}: {
  title: string
  column: string
  hint?: string
  rows: T[]
  extra?: { heads: string[]; cells: (row: T) => string[] }
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>{title}</h2>
        {hint ? <span className="hint">{hint}</span> : null}
      </div>
      <div className="card-body flush">
        <Panel isLoading={false} error={null} isEmpty={rows.length === 0} emptyText="No outcomes recorded in this period.">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{column}</th>
                  {extra?.heads.map((head) => (
                    <th key={head} className="num">
                      {head}
                    </th>
                  ))}
                  <th className="num">Attempted</th>
                  <th className="num">Delivered</th>
                  <th className="num">Partial</th>
                  <th className="num">Failed</th>
                  <th className="num">Success</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.name}>
                    <td className="strong">{row.name}</td>
                    {extra?.cells(row).map((cell, index) => (
                      <td key={index} className="num">
                        {cell}
                      </td>
                    ))}
                    <td className="num">{count(row.attempts)}</td>
                    <td className="num">{count(row.delivered)}</td>
                    <td className="num">{count(row.partial)}</td>
                    <td className="num">{count(row.undelivered)}</td>
                    <td className="num strong">{percent(successPct(row), 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}
